/**
 * Analytics consent, stored per browser.
 *
 * The strict reading of GDPR/ePrivacy is that analytics cookies need opt-in
 * *before* anything loads — so this gates whether gtag.js is injected at all,
 * rather than loading it and asking it not to store things. Nothing is
 * requested, and no cookie is set, until someone accepts.
 */
export type Consent = "granted" | "denied";

const STORAGE_KEY = "cookie-consent";

function isConsent(value: unknown): value is Consent {
  return value === "granted" || value === "denied";
}

/** `null` means undecided — the banner has not been answered yet. */
export function readConsent(): Consent | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isConsent(stored) ? stored : null;
  } catch {
    // Storage blocked. Undecided is the safe answer: it means "not granted",
    // so nothing loads, and the banner will simply ask again next visit.
    return null;
  }
}

export function writeConsent(consent: Consent): void {
  try {
    localStorage.setItem(STORAGE_KEY, consent);
  } catch {
    /* choice won't persist, but it still applies for this page view */
  }
}

export function clearConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * An explicit Do Not Track signal answers the question already, so the banner
 * is never shown — asking again would be nagging someone who has told the
 * browser their preference.
 */
export function hasDoNotTrack(): boolean {
  if (typeof navigator === "undefined") return false;
  const dnt =
    navigator.doNotTrack ??
    (window as { doNotTrack?: string }).doNotTrack ??
    (navigator as { msDoNotTrack?: string }).msDoNotTrack;
  return dnt === "1" || dnt === "yes";
}

/** GA4 cookies, so a decline after an accept actually removes them. */
export function clearAnalyticsCookies(measurementId: string): void {
  if (typeof document === "undefined") return;

  // _ga is the client id; _ga_<container> is the session. Both are set on the
  // registrable domain, so they must be expired against it and its parent.
  const suffix = measurementId.replace(/^G-/, "");
  const names = ["_ga", `_ga_${suffix}`, "_gid"];
  const host = window.location.hostname;
  const domains = [host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];

  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
    }
    document.cookie = `${name}=; Max-Age=0; path=/`;
  }
}
